import { defineStore } from 'pinia';
import { ref, computed, nextTick } from 'vue';
import { CodeEngineCoin2CoreClient, useCodeEngineStore } from '@daland_cuso/code-engine-js-client';
import { PhaseTypes } from '@/constants/phaseTypes';
import { DigitalAssetTypeNames } from '@/constants/digitalAssetTypes';
import { useRouter } from 'vue-router';

const clientId = 'DL';
//const apiUrl = 'https://dcce-stcloud-code-engine-api-test.azurewebsites.net/';
//const apiUrl = 'https://code-engine-api-dev.azurewebsites.net/';
//const apiUrl = 'https://api-code-engine-test.dalandcuso.com/';
// const apiUrl = 'https://localhost:7136/';
//const apiKey = 'STC2CKEY';
const apiUrl = import.meta.env.VITE_CODE_ENGINE_API_URL;
const apiKey = import.meta.env.VITE_CODE_ENGINE_API_KEY;

export const useCoin2CoreStore = defineStore('coin2Core', () => {  
  const router = useRouter();
  const codeEngineStore = useCodeEngineStore();

  const username = ref('');
  const sessionId = ref('');
  const error = ref('');
  const personId = ref('');
  const loginId = ref('');
  const configuration = ref({});
  const creditUnionName = ref('');
  const creditUnionEmail = ref('');
  const creditUnionPhone = ref('');
  const configuredCurrencies = ref([]);
  const cryptoWallets = ref([]);
  const branding = ref({});  
  const features = ref([]);
  const emails = ref([]);
  const shares = ref([]);
  const phoneNumbers = ref([]);
  const showSecondaryAuth = ref(false);
  const secondaryAuthAction = ref('');
  const secondaryAuthRequestId = ref('');
  const secondaryAuthSuccess = ref(false);
  const codeEngineCoin2CoreClient = ref(new CodeEngineCoin2CoreClient());
  const sendCodeFlow = ref(true);
  const verifyCodeFlow = ref(false);
  const portalTypeId = ref(5);
  const passwordChangeRequired = ref(false);
  const transactEnabled = ref(false);
  const phaseEligibility = ref([]);

  function getWalletByType(currencyId) {
    // Vaults are keyed by DigitalAssetType (vaultType on the wire), not by the
    // currency core id, so resolve the currency's assetType before matching.
    // Callers passing a raw asset type (e.g. Payments' Bitcoin lookup) still
    // work via the fallback when no currency record matches the id.
    const currency = configuredCurrencies.value.find((c) => c.id === currencyId);
    const assetType = currency?.assetType ?? currencyId;
    return cryptoWallets.value.find((w) => w.vaultType === assetType) || {};
  };

  function getCurrencyByType(currencyId) {
    // Prefer the currency core id; fall back to the DigitalAssetType value for
    // callers that key by asset type (the two coincide on some cores but not all).
    let currency = configuredCurrencies.value.find((c) => c.id === currencyId);
    if(!currency) {
      currency = configuredCurrencies.value.find((c) => c.assetType === currencyId);
    }
    return currency || null;
  };

  function getCurrencyUsdValue(currencyId) {
    const currency = getCurrencyByType(currencyId);
    return currency?.currentUsdValue || 0;
  };

  // Bitcoin display unit helpers
  function btcToSat(btcAmount) {
    // 1 BTC = 100,000,000 satoshis
    return Math.round(btcAmount * 100000000);
  }

  function satToBtc(satAmount) {
    return satAmount / 100000000;
  }

  function formatBitcoinAmount(btcAmount, unit = 'btc', includeLabel = true) {
    if (unit === 'sat') {
      const sats = btcToSat(btcAmount);
      return includeLabel ? `${sats.toLocaleString()} SAT` : sats.toLocaleString();
    }
    return includeLabel ? `${btcAmount.toFixed(8)} BTC` : btcAmount.toFixed(8);
  }

  function isPhaseEligible(assetType, phase) {
    // phaseEligibility.digitalAssetTypeId carries the DigitalAssetType enum NAME
    // (VaultType.ToString() server-side), while vaults/currencies carry the
    // numeric value - accept either representation.
    const entry = phaseEligibility.value.find(
      e => e.digitalAssetTypeId === DigitalAssetTypeNames[assetType]
        || String(e.digitalAssetTypeId) === String(assetType)
    );
    if (!entry) return false;
    switch (phase) {
      case PhaseTypes.VAULTING: return entry.isVaultingEligible;
      case PhaseTypes.DEPOSIT: return entry.isDepositEligible;
      case PhaseTypes.EXCHANGE: return entry.isExchangeEligible;
      case PhaseTypes.TRANSACT: return entry.isTransactEligible;
      default: return false;
    }
  }

  const portalTimeout = computed(() => {            
    if(configuration.value && configuration.value.portalTimeout)  {
      return configuration.value.portalTimeout;
    } else {
      return 10; // Default to 10 min
    }
  });

  const noValuationSources = computed(() => {
    let toReturn = true;

    for(let i = 0; i < configuredCurrencies.value.length; i++) {
      if(configuredCurrencies.value[i].currentUsdValue) {
        toReturn = false;
      }
    }
    return toReturn;
  });
      
  const portalBranding = computed(() => {
    if(codeEngineStore.branding) {
      return codeEngineStore.branding;
    } else {
      return {};
    }
  })

  const portalTheme = computed(() => {
    if(codeEngineStore.theme) {
      return codeEngineStore.theme;
    } else {
      return {};
    }
  })
  const personContacts = computed(() => {
    let personContacts = [];
    if(phoneNumbers.value) {
      for(let i = 0; i < phoneNumbers.value.length; i++) {        
        let contact = phoneNumbers.value[i];
        let newContact = {};          
        newContact.name = contact.typeDescription + ' - '  + contact.maskedNumber;          
        newContact.coreId = contact.coreId;

        if(contact.maskedNumber && configuration.value.smsEnabled) {
          personContacts.push(newContact);
        }
      }
    }
  

    if(emails.value) {
      for(let i = 0; i < emails.value.length; i++) {        
        let contact = emails.value[i];
        let newContact = {};          
        newContact.name = contact.typeDescription + ' - '  + contact.maskedEmailAddress;          
        newContact.coreId = contact.coreId;

        if(contact.maskedEmailAddress && configuration.value.emailEnabled) {
          personContacts.push(newContact);
        }
      }
    }
    return personContacts;
  })

  const isFeatureAvailable = ((featureName) => {
    if(features.value) {
      let feature = features.value.find(x => x.name === featureName);
      if(feature) {
        return feature.isEnabled;
      } else {
        return false; // If it's not found we assume it's NOT enabled
      }
    }
  })

  const featureRequiresSecondaryAuth = ((featureName) => {      
    if(features.value) {
      let feature = features.value.find(x => x.name === featureName);
      if(feature) {
        return feature.secondaryAuthenticationRequired;
      } else {
        return false; // If it's not found, we assume it's not set, therefore not required
      }
    }
  })

  const assetsForSale = computed(() => {
    let toReturn = [];

    let mapped = configuredCurrencies.value.filter(x => x.sellEnabled === true);    
    toReturn.push(...mapped);

    return toReturn;
  })

  const assetsForPurchase = computed(() => {
    let toReturn = [];

    let mapped = configuredCurrencies.value.filter(x => x.buyEnabled === true);    
    toReturn.push(...mapped);
    
    return toReturn;
  })

  const exchangeEnabled = computed(() => {
    let toReturn = false;

    if(isFeatureAvailable('BuyDigitalAssets')) {      
      if(assetsForPurchase.value.length > 0) {
        toReturn = true;
      }
    }
    
    if(isFeatureAvailable('SellDigitalAssets')) {      
       if(assetsForSale.value.length > 0)  {
        toReturn = true;
      }
    }

    return toReturn;
  })

  async function startSession() {
    codeEngineCoin2CoreClient.value.init(clientId, apiUrl, apiKey);
    let resp = await codeEngineCoin2CoreClient.value.startSession();
    codeEngineStore.init(clientId, apiUrl, apiKey);
    if(resp.sessionId) {
      sessionId.value = resp.sessionId;
    } else {
      error.value = resp.error;
    }
  }

  async function getConfiguration() {
    let resp = await codeEngineCoin2CoreClient.value.getConfiguration();
    let loadBranding = await codeEngineStore.loadPortalBranding(portalTypeId.value);
    let loadTheme = await codeEngineStore.loadPortalTheme(portalTypeId.value);
    let portalContentResp = await codeEngineStore.loadPortalContent(portalTypeId.value);
    let errorMappingResp = await codeEngineStore.loadErrorMapping(portalTypeId.value);
    
    if(resp.success) {
      configuration.value = resp;
      creditUnionName.value = resp.creditUnionName;
      creditUnionPhone.value = resp.creditUnionPhone;
      creditUnionEmail.value = resp.creditUnionEmail;
      features.value = resp.features;       
      transactEnabled.value = resp.transactEnabled;
      
      return resp;
    } else {
      error.value = "Unable to connect to services";
    }
  }

  async function validateIpAddress() {
    let resp = await codeEngineCoin2CoreClient.value.validateIpAddress();

    if(resp.success) { 
      return resp.isValidIp; 
    } else {
      return false;
    }
  }

  async function loginUser(un, pwd) {
    let resp = await codeEngineCoin2CoreClient.value.login(un, pwd);    
    if(resp.success) {      
      username.value = un;
      personId.value = resp.personId;
      loginId.value = resp.loginId;
      if(resp.personDetails) {
        emails.value = resp.personDetails.emails;
        phoneNumbers.value = resp.personDetails.phoneNumbers;
        shares.value = resp.personDetails.shares;
      }

      if(resp.passwordChangeRequired) {
        passwordChangeRequired.value = true;
      }
      return true;
    } else {      
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      
      return false;
    }    
  }

  async function changeLoginPassword(oldPwd, newPwd) {
    let resp = await codeEngineCoin2CoreClient.value.changeLoginPassword(username.value, oldPwd, newPwd, portalTypeId.value);
    if(resp.success) {
      return true;
    } else {
      // This can have core specific messages around bad password stuff we want to preserve
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode, resp.errorMessage);
      return false;
    }
  }

  async function logoutUser() {
    loginId.value = '';
    personId.value = '';
    emails.value = [];
    phoneNumbers.value = [];
    username.value = '';    
    router.push( { name: 'login' });
  }  

  async function registerUser(username, password, taxId, dateOfBirth) {
    let resp = await codeEngineCoin2CoreClient.value.registerUser(username, password, taxId, dateOfBirth);    
    if(resp.success) {        
      return true;
    } else {
      // This can have core specific messages around bad password stuff we want to preserve
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode, resp.errorMessage);
      return false;
    }
  }

  async function sendSecondaryAuthRequest(contactId) {
    let resp = await codeEngineCoin2CoreClient.value.sendSecondaryAuthRequest(username.value, contactId, secondaryAuthAction.value, loginId.value, portalTypeId.value);    
    if(resp.success) {
      secondaryAuthRequestId.value = resp.requestId;
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function verifySecondaryAuthRequest(contactId, passcode) {
    let resp = await codeEngineCoin2CoreClient.value.verifySecondaryAuthRequest(secondaryAuthRequestId.value, username.value, contactId, secondaryAuthAction.value, loginId.value, passcode, portalTypeId.value);    
    if(resp.success) {
      return true;
    } else {        
      if(resp.logoutUser) {
        showSecondaryAuth.value = false;
        logoutUser();        
        error.value = codeEngineStore.getPortalContentByPageAndContentName('SecondaryAuthentication', 'lock_out_popup_error'); //'You have failed secondary authentication, your account has been locked. Please contact someone who cares.';
      } else {           
        error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
        return false;
      }
    }
  }
  
  async function getCryptoCurrencies() {
    let resp = await codeEngineCoin2CoreClient.value.getConfiguredCurrencies();
    if(resp.success) {      
      configuredCurrencies.value = resp.currencies;
      
      let exchangeAvailabilityResp = await codeEngineCoin2CoreClient.value.getExchangeAvailability();
      if(exchangeAvailabilityResp.success) {
        if(exchangeAvailabilityResp.availableForSale) {
          for (const [key, value] of Object.entries(exchangeAvailabilityResp.availableForSale)) {
            let matchingCurrency = configuredCurrencies.value.find(x => x.abbreviation === key);
            if(matchingCurrency) {
              matchingCurrency.sellEnabled = true;
            }
          }
        }
        if(exchangeAvailabilityResp.availableForPurchase) {
          for (const [key, value] of Object.entries(exchangeAvailabilityResp.availableForPurchase)) {
            let matchingCurrency = configuredCurrencies.value.find(x => x.abbreviation === key);
            if(matchingCurrency) {
              matchingCurrency.buyEnabled = true;
            }
          }
        }
      }
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
    }
  }
    
  async function getCryptoWallet() {
    let resp = await codeEngineCoin2CoreClient.value.getCryptoBalances(personId.value);
    if(resp.success) {
      cryptoWallets.value = resp.digitalAssetVaults;
      phaseEligibility.value = resp.phaseEligibility || [];
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
    }
  }

  // Vault history for the whole person via the live personhistory endpoint
  // (the per-vault api/c2c/history endpoint is disabled server-side) - callers
  // filter the buckets by vaultId
  async function getPersonVaultHistory() {
    const resp = await codeEngineCoin2CoreClient.value.getPersonVaultHistory(personId.value);
    if (resp && resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp?.errorCode);
      return null;
    }
  }

  async function getEstimatedFees(walletId, coinAmount, destinationAddress) {
    let resp = await codeEngineCoin2CoreClient.value.getEstimatedFees(personId.value, walletId, coinAmount, destinationAddress);
    if(resp.success) {            
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function createVaultWithdraw(walletId, coinAmount, destinationAddress, feeAmount) {
    let resp = await codeEngineCoin2CoreClient.value.createVaultWithdraw(personId.value, walletId, coinAmount, destinationAddress, feeAmount);
    if(resp.success) {            
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function evaluateVaultWithdraw(walletId, coinAmount, recipientAddress, feeAmount, usdValuation, usdRate) {
    try {
      let resp = await codeEngineCoin2CoreClient.value.evaluateVaultWithdraw(
        personId.value,
        walletId,
        coinAmount,
        recipientAddress,
        feeAmount,
        usdValuation,
        usdRate
      );

      if (resp.success) {
        return {
          success: true,
          holdHours: resp.holdHours,
          releaseDateTime: resp.releaseDateTime,
          hasHoldPeriod: resp.holdHours > 0,
          usdValuationAtSubmission: resp.usdValuationAtSubmission,
          // Pass-through for confirm step
          matchedTierId: resp.matchedTierId,
          matchReason: resp.matchReason,
          contentParameters: resp.contentParameters
        };
      } else {
        error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
        return { success: false, errorMessage: resp.errorMessage };
      }
    } catch (err) {
      console.error('Evaluate withdrawal error:', err);
      return { success: false, errorMessage: 'Failed to evaluate withdrawal' };
    }
  }

  async function confirmVaultWithdraw(walletId, coinAmount, recipientAddress, feeAmount, usdValuation, usdRate, holdHours, matchedTierId, matchReason) {
    try {
      let resp = await codeEngineCoin2CoreClient.value.confirmVaultWithdraw(
        personId.value,
        walletId,
        coinAmount,
        recipientAddress,
        feeAmount,
        usdValuation,
        usdRate,
        holdHours,
        matchedTierId,
        matchReason
      );

      if (resp.success) {
        return {
          success: true,
          withdrawalId: resp.withdrawalId,
          transactionId: resp.transactionId,
          holdHours: resp.holdHours,
          releaseDateTime: resp.releaseDateTime,
          hasHoldPeriod: resp.holdHours > 0,
          usdValuationAtSubmission: resp.usdValuationAtSubmission,
          contentParameters: resp.contentParameters
        };
      } else {
        error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
        return { success: false, errorMessage: resp.errorMessage };
      }
    } catch (err) {
      console.error('Confirm withdrawal error:', err);
      return { success: false, errorMessage: 'Failed to confirm withdrawal' };
    }
  }

  async function addContactInfo(newValue, newCategory) {
    let resp = await codeEngineCoin2CoreClient.value.addContactInfo(personId.value, newValue, newCategory);
    if(resp.success) {      
      if(newCategory.indexOf('email') > -1) {
        let newEmail = {};
        newEmail.coreId = resp.coreId;
        newEmail.emailAddress = newValue;
        newEmail.typeDescription = newCategory;
        let splitEmail = newValue.split('@');        
        newEmail.maskedEmailAddress = splitEmail[0][0] + '****' + splitEmail[0][splitEmail[0].length - 1] + '@' + splitEmail[1];        
        emails.value.push(newEmail);
      } else {
        let newPhoneNumber = {};
        newPhoneNumber.coreId = resp.coreId;
        newPhoneNumber.number = newValue;
        newPhoneNumber.typeDescription = newCategory;
        newPhoneNumber.maskedNumber = 'XXX-XXX-X' + newValue.substring(newValue.length - 3);
        phoneNumbers.value.push(newPhoneNumber);
      }
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function updateContactInfo(contactId, newValue, newCategory) {
    let resp = await codeEngineCoin2CoreClient.value.updateContactInfo(personId.value, contactId, newValue, newCategory);
    if(resp.success) {      
      let phoneIdx = phoneNumbers.value.findIndex(x => x.coreId == contactId);
      if(phoneIdx > -1) {        
        phoneNumbers.value[phoneIdx].maskedNumber = 'XXX-XXX-X' + newValue.substring(newValue.length - 3);;
      }

      let emailIdx = emails.value.findIndex(x => x.coreId == contactId);
      if(emailIdx > -1) {        
        let splitEmail = newValue.split('@');        
        emails.value[emailIdx].maskedEmailAddress = splitEmail[0][0] + '****' + splitEmail[0][splitEmail[0].length - 1] + '@' + splitEmail[1];        
      }

      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function deleteContactInfo(contactId) {
    let resp = await codeEngineCoin2CoreClient.value.deleteContactInfo(personId.value, contactId);
    if(resp.success) {              
      let phoneIdx = phoneNumbers.value.findIndex(x => x.coreId == contactId);
      if(phoneIdx > -1) {
        phoneNumbers.value.splice(phoneIdx, 1);
      }

      let emailIdx = emails.value.findIndex(x => x.coreId == contactId);
      if(emailIdx > -1) {
        emails.value.splice(emailIdx, 1);
      }
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function sendForgotPasswordLink(un, phone, email, ssn) {
    let resp = await codeEngineCoin2CoreClient.value.sendForgotPasswordLink(un, phone, email, ssn, portalTypeId.value, location.origin);
    if(resp.success) {
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function validatePasswordResetLink(rid, rtk) {
    let resp = await codeEngineCoin2CoreClient.value.validatePasswordResetLink(rid, rtk);
    if(resp.success) {
      return resp.requestId;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function completeForgotPassword(requestId, pwd) {
    let resp = await codeEngineCoin2CoreClient.value.completeForgotPassword(requestId, pwd, portalTypeId.value);
    if(resp.success) {
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode, resp.errorMessage);
      return false;
    }
  }

  async function getWalletHistory(walletId) {
    let resp = await codeEngineCoin2CoreClient.value.getWalletHistory(personId.value, walletId) 
    if(resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return null;
    }
  }  

  async function cancelVaultWithdraw(walletId, withdrawId) {
    // TEMP direct post: the installed js client (0.0.85) still sends `walletId` where the
    // API contract is `vaultId`. Switch back to codeEngineCoin2CoreClient.cancelVaultWithdraw
    // once the vaultId-aligned client version is published and this package is bumped.
    let resp = await codeEngineCoin2CoreClient.value.completePostCall('api/c2c/cancelwithdraw', {
      personId: personId.value,
      vaultId: walletId,
      withdrawId: withdrawId
    });
    if(resp && resp.success) {
      return true;
    } else {
      error.value = resp?.errorMessage || codeEngineStore.getErrorMappingByErrorCode(resp?.errorCode);
      return false;
    }
  }

  async function startSecondaryAuthentication(action) {
    // To bypass secondary auth locally, set VITE_BYPASS_SECONDARY_AUTH=true in .env.local
    if (import.meta.env.VITE_BYPASS_SECONDARY_AUTH !== 'true') {
      secondaryAuthSuccess.value = false;
      secondaryAuthAction.value = action;
      showSecondaryAuth.value = true;
      sendCodeFlow.value = true;
      verifyCodeFlow.value = false;
    } else {
      console.warn('[DEV] Secondary authentication bypassed via VITE_BYPASS_SECONDARY_AUTH');
      // I have to make it unsuccessful, then successful to trigger the watches
      secondaryAuthSuccess.value = false;
      await nextTick();
      secondaryAuthAction.value = action;
      secondaryAuthSuccess.value = true;
    }
  }

  async function sendPersonWallets(walletId, destinationAddress) {
    console.info('destination address', destinationAddress);
    let fullContact = emails.value.find(x => x.coreId === destinationAddress);    
    console.info('full contact', fullContact);
    if(fullContact) {
      let resp = await codeEngineCoin2CoreClient.value.sendWalletQrCodeEmail(personId.value, [walletId], fullContact.emailAddress, null);
      return resp.success;
    } else {
      fullContact = phoneNumbers.value.find(x => x.coreId === destinationAddress);
      console.info('full contact phone', fullContact);
      let resp = await codeEngineCoin2CoreClient.value.sendWalletQrCodeSms(personId.value, [walletId], null, fullContact.number);
      return resp.success;
    }    
  }

  function finishSecondaryAuthentication() {    
    showSecondaryAuth.value = false;
    secondaryAuthSuccess.value = true;          
  }  

  async function updateTransactStatus(
    vaultId,
    status,
    liquidityPoolOverride,
    nativePoolAmountOverride,
    nativeTransactionAmountOverride,
    usdTransactionAmountOverride
  ) {
    const resp = await codeEngineCoin2CoreClient.value.updateVaultTransactStatus(
      personId.value,
      vaultId,
      status,
      liquidityPoolOverride,
      nativePoolAmountOverride,
      nativeTransactionAmountOverride,
      usdTransactionAmountOverride
    );

    if (resp.success) {
      return true;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return false;
    }
  }

  async function quoteExchange(assetType, nativeAmount, usdAmount, shareId, buy) {
    const resp = await codeEngineCoin2CoreClient.value.quoteDigitalAssetExchange(assetType, personId.value, nativeAmount, usdAmount, shareId, buy);

    if(resp && resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp?.errorCode);
      return { success: false, errorMessage: resp?.errorMessage };
    }
  }

  async function buyDigitalAssets(vaultId, shareId, nativeAmount, usdAmount) {
    const resp = await codeEngineCoin2CoreClient.value.purchaseDigitalAssets(personId.value, vaultId, shareId, nativeAmount, usdAmount);

    if(resp && resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp?.errorCode);
      return { success: false, errorMessage: resp?.errorMessage };
    }
  }

  async function sellDigitalAssets(vaultId, shareId, nativeAmount, usdAmount) {
    const resp = await codeEngineCoin2CoreClient.value.sellDigitalAssets(personId.value, vaultId, shareId, nativeAmount, usdAmount);

    if(resp && resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp?.errorCode);
      return { success: false, errorMessage: resp?.errorMessage };
    }
  }

  async function decodePayment(invoiceString) {    
    const resp = await codeEngineCoin2CoreClient.value.decodePayment('LNBTC', invoiceString);
    
    if(resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return null;
    }
  }

  async function payInvoice(paymentString, personId, vaultId) {
    const resp = await codeEngineCoin2CoreClient.value.payInvoice('LNBTC', paymentString, personId, vaultId)

    if(resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return null;
    }
  }

  async function createInvoice(description, paymentAmount, personId, vaultId) {
    const resp = await codeEngineCoin2CoreClient.value.generateInvoice('LNBTC', description, paymentAmount, personId, vaultId);

    if(resp.success) {
      return resp;
    } else {
      error.value = codeEngineStore.getErrorMappingByErrorCode(resp.errorCode);
      return null;
    }
  }

  return { 
    startSession,
    getConfiguration,
    getCryptoCurrencies,    
    validateIpAddress,
    loginUser,
    changeLoginPassword,
    startSecondaryAuthentication,
    finishSecondaryAuthentication,
    error, // Note, this should probably get moved out, and not just returned as a propery
    configuration,
    creditUnionName,
    creditUnionEmail,
    creditUnionPhone,    
    secondaryAuthAction,
    featureRequiresSecondaryAuth,
    isFeatureAvailable,
    noValuationSources,
    updateContactInfo,
    addContactInfo,
    deleteContactInfo,
    registerUser,
    getWalletByType,
    getCurrencyByType,
    getCurrencyUsdValue,
    btcToSat,
    satToBtc,
    formatBitcoinAmount,
    isPhaseEligible,
    phaseEligibility,
    getCryptoWallet,
    showSecondaryAuth,
    secondaryAuthSuccess,
    configuredCurrencies,
    portalBranding,
    portalTheme,
    sendSecondaryAuthRequest,
    verifySecondaryAuthRequest,
    personContacts,
    shares,
    portalTimeout,
    loginId,
    logoutUser,
    getEstimatedFees,
    createVaultWithdraw,
    evaluateVaultWithdraw,
    confirmVaultWithdraw,
    emails, 
    phoneNumbers,
    sendCodeFlow,
    verifyCodeFlow,
    passwordChangeRequired,    
    getPortalContentByPageName: codeEngineStore.getPortalContentByPageName, 
    getPortalContentByPageAndContentName: codeEngineStore.getPortalContentByPageAndContentName,    
    sendForgotPasswordLink,
    validatePasswordResetLink,
    completeForgotPassword,
    getWalletHistory,
    getPersonVaultHistory,
    cancelVaultWithdraw,
    sendPersonWallets,
    updateTransactStatus,
    quoteExchange,
    buyDigitalAssets,
    sellDigitalAssets,
    assetsForPurchase,
    assetsForSale,
    exchangeEnabled,
    transactEnabled,

    decodePayment,
    createInvoice,
    payInvoice
   }
})
